import * as React from "react";
import styled, { css } from "styled-components";

import {
  Colors,
  ThemeType,
  FontColors
} from "src/library/utils";

import { Text } from "../Text";
import MoonLoaderSpinner from "./MoonLoaderSpinner";
import PuffLoaderSpinner from "./PuffLoaderSpinner";
import PulseLoaderSpinner from "./PulseLoaderSpinner";

type LoadingSpinnerPositioningType = "standalone" | "inline";
type LoadingSpinnerTypesType = "circular" | "flat" | "circular-pulse";

interface LoadingSpinnerProps extends Pick<React.HTMLAttributes<HTMLDivElement>, "className"> {
  position?: LoadingSpinnerPositioningType;
  type: LoadingSpinnerTypesType;
  size?: number;
  color?: keyof Colors;
  offsetTop?: number;
  message?: string;
  fontColor?: keyof FontColors;
}

interface ILoadingSpinnerWrapper {
  position?: LoadingSpinnerPositioningType;
  offsetTop: number;
}

export interface ILoaderStyled {
  customColor: keyof Colors;
  theme: ThemeType;
}

interface IDynamicSpinner {
  type: LoadingSpinnerTypesType;
  size?: number;
  color: keyof Colors;
}

/**
 * Returns a loading spinner according to the given type
 */
const DynamicSpinner = ({
  type, size, color
}: IDynamicSpinner) => {
  switch (type) {
    default:
    case "circular":
      return (
        <MoonLoaderSpinner
          size={size}
          color={color}
        />
      );

    case "circular-pulse":
      return (
        <PuffLoaderSpinner
          size={size}
          color={color}
        />
      );

    case "flat":
      return (
        <PulseLoaderSpinner
          size={size}
          color={color}
        />
      );
  }
};

/**
 * Outputs a loading spinner and variations depending on it's position
 */
const LoadingSpinner: React.ReactFCWithChildren<LoadingSpinnerProps> = ({
  className,
  position,
  type,
  offsetTop,
  size,
  color,
  message,
  fontColor
}) => {
  return (
    <LoadingSpinnerWrapper
      className={className}
      position={position}
      offsetTop={offsetTop as number}
    >
      <div className="loading-spinner-content">
        <DynamicSpinner
          type={type}
          color={color as keyof Colors}
          size={size}
        />

        {message && (
          <Text
            color={fontColor || "primary"}
            className="loading-message"
          >
            {message}
          </Text>
        )}
      </div>
    </LoadingSpinnerWrapper>
  );
};

LoadingSpinner.defaultProps = {
  position: "standalone",
  color: "midGrey",
  offsetTop: 0
};

export default LoadingSpinner;

const LoadingSpinnerWrapper = styled.div<ILoadingSpinnerWrapper>`
  * {
    box-sizing: content-box !important;
  }

  ${({ position, offsetTop }) => PositioningCss({
    position,
    offsetTop
  })};

  .loading-spinner-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    [class*="ballStyle-ball"] {
      opacity: 1;
    }

    [class*="ballStyle-circle-circle"] {
      opacity: 0.2;
    }

    .loading-message {
      display: block;
      padding-top: 0.4rem;
    }
  }
`;

const PositioningStandaloneCss = (offsetTop: number) => css`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: ${`${offsetTop}px`};
  left: 0;
  width: 100%;
  height: calc(100% - ${`${offsetTop}px`});
`;

const PositioningInlineCss = (offsetTop: number) => css`
  display: block;
  padding-top: ${`${offsetTop}px`};
  text-align: left;

  .loading-message {
    text-align: center;
  }
`;

/**
 * Assigns "position" CSS accordantly
 */
const PositioningCss = ({ position, offsetTop }: ILoadingSpinnerWrapper) => {
  switch (position) {
    default:
    case "standalone":
      return PositioningStandaloneCss(offsetTop);

    case "inline":
      return PositioningInlineCss(offsetTop);
  }
};
